/* eslint-disable max-lines-per-function */
import debounce from '@mvps-genesis/debounce';
import ParameterValidator from '@mvps-genesis/parameter-validator';

export default class Autocomplete {

	static searchBarElements;

	static client;

	static app;

	static create ({searchBarElements, searchButtonElements, defaultSearchParams}) {

		const loadVueApp = async (initialQuery = '') => {
			const {AutocompleteClient} = await import('./api');
			const {default: Vue} = await import('vue');
			const {default: VueLazyload} = await import('vue-lazyload');
			const {rallyHouseFilters} = await import('./data/bgfilters');

			// Create client instance
			this.client = new AutocompleteClient(defaultSearchParams);

			// Apply Bgfilters
			rallyHouseFilters(this.client);

			// ====================================================

			const {default: AutocompleteApp} = await import('./components/autocomplete/AutocompleteApp');
			const {default: toCurrency} = await import('./filters/toCurrency');
			const {default: rewriteUrl} = await import('./filters/rewriteUrl');

			// Plugins
			Vue.use(VueLazyload, {
				attempt: 1
			});

			// Filters
			Vue.filter('toCurrency', toCurrency);
			Vue.filter('rewriteUrl', rewriteUrl);

			// Create new Vue app
			this.app = new Vue({
				data: {
					query: initialQuery,
					showDrawer: false,
					trendingResults: this.trending
				},
				el: '#autocomplete-main',
				render (createElement) {
					return createElement(AutocompleteApp, {
						props: {
							query: this.query,
							showDrawer: this.showDrawer
						}
					});
				}
			});

			return true;
		};

		// Create parameter validator instance
		const validator = new ParameterValidator(this);

		// Load passed search elements
		this.searchBarElements = validator.isElementList('searchBarElements', searchBarElements);
		this.searchButtonElements = validator.isElementList('searchButtonElements', searchButtonElements);

		const syncAriaExpanded = () => {
			const isVisible = this.app?.showDrawer && this.app?.query?.length > 0;

			for (let searchBarElement of this.searchBarElements) {
				searchBarElement.setAttribute('aria-expanded', String(!!isVisible));
			}
		};

		// Define close method
		const close = () => {
			if (!this.app) {
				return;
			}

			this.app.showDrawer = false;

			syncAriaExpanded();

			document.removeEventListener('click', close);
		};

		// Open
		const open = () => {
			if (!this.app) {
				return;
			}

			if (this.app.showDrawer === true) {
				return;
			}

			this.app.showDrawer = true;

			syncAriaExpanded();

			document.addEventListener('click', close);
		};

		// Define helper method
		const stopProp = (e) => e.stopPropagation();

		// Create focus handler
		const onFocus = async (e) => {
			if (this.app === void 0 || this.app === null) {
				await loadVueApp(e.target.value);
			}

			open(e.target);
		};

		// Create keyup handler
		const onKeyup = debounce(
			300,
			(e) => {
				// Set the query
				this.app.query = e.target.value;

				// Open search preview
				open(e.target);

				syncAriaExpanded();

				// Sync values between other search bars
				for (let searchBarElement of this.searchBarElements) {
					if (e.target !== searchBarElement) {
						searchBarElement.value = e.target.value;
					}
				}
			}
		);

		// Handle events for searchbars
		for (let searchBarElement of this.searchBarElements) {
			searchBarElement.addEventListener('keyup', onKeyup);
			searchBarElement.addEventListener('focus', onFocus, {once: true});
			searchBarElement.addEventListener('click', stopProp);
		}

		// Create events for searchbuttons
		for (let searchButtonElement of this.searchButtonElements) {
			searchButtonElement.addEventListener('click', stopProp);
		}

	}

}
